import { Users } from "lucide-react";
import { Link } from "react-router";
import { formatPersonName } from "~/lib/format";
import { computeOwnershipSplit } from "~/lib/ownership";
import { PERSON_REDACTED_NOTICE, personPath } from "~/lib/person-profile";
import type { CompanyProfile, Officer } from "~/types/company";
import { CardFooterLink, OverviewCard } from "./card";

const DIRECTOR_ROLES = new Set(["STATUTORY_DIRECTOR"]);

function pickDirectors(officers: Officer[]): { label: string; officers: Officer[] } | null {
	const active = officers.filter((o) => o.isActive);
	if (active.length === 0) return null;
	const directors = active.filter((o) => DIRECTOR_ROLES.has(o.role));
	if (directors.length > 0) {
		return { label: directors.length === 1 ? "Jednatel" : "Jednatelé", officers: directors };
	}
	return { label: "Statutární orgán", officers: active };
}

function formatPct(value: number): string {
	return value.toLocaleString("cs-CZ", { maximumFractionDigits: 1 });
}

/** ④ Lidé a vlastníci — directors + natural-vs-legal-person ownership split bar. */
export function PeopleCard({ company }: { company: CompanyProfile }) {
	const directors = pickDirectors(company.officers);
	const split = computeOwnershipSplit(company.shareholders);

	if (!directors && !split) return null;

	return (
		<OverviewCard icon={Users} title="Lidé a vlastníci" hue="violet">
			<div className="flex flex-col gap-4">
				{directors && (
					<div className="flex flex-col gap-1">
						<p className="text-xs font-medium text-muted-foreground">{directors.label}</p>
						<p className="text-sm font-medium">
							{directors.officers.map((o, index) => {
								const person = o.redacted ? null : o.person;
								return (
									<span key={o.id}>
										{index > 0 && ", "}
										{person == null ? (
											<span className="font-normal italic text-muted-foreground">
												{PERSON_REDACTED_NOTICE}
											</span>
										) : o.personId ? (
											<Link
												to={personPath(o.personId, formatPersonName(person))}
												prefetch="intent"
												className="text-primary hover:underline"
											>
												{formatPersonName(person)}
											</Link>
										) : (
											formatPersonName(person)
										)}
									</span>
								);
							})}
						</p>
					</div>
				)}

				{split && (
					<div className="flex flex-col gap-2">
						<p className="text-xs font-medium text-muted-foreground">Vlastnická struktura</p>
						<div
							className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
							role="img"
							aria-label={`Fyzické osoby ${formatPct(split.personsPct)} %, právnické osoby ${formatPct(split.entitiesPct)} %`}
						>
							{split.personsPct > 0 && (
								<div className="h-full bg-primary-500" style={{ width: `${split.personsPct}%` }} />
							)}
							{split.entitiesPct > 0 && (
								<div className="h-full bg-primary-200" style={{ width: `${split.entitiesPct}%` }} />
							)}
						</div>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
							{split.personsPct > 0 && (
								<span className="inline-flex items-center gap-1.5">
									<span className="h-2 w-2 rounded-full bg-primary-500" />
									Fyzické osoby {formatPct(split.personsPct)} %
								</span>
							)}
							{split.entitiesPct > 0 && (
								<span className="inline-flex items-center gap-1.5">
									<span className="h-2 w-2 rounded-full bg-primary-200" />
									Právnické osoby {formatPct(split.entitiesPct)} %
								</span>
							)}
						</div>
					</div>
				)}
			</div>
			<CardFooterLink to="lide">Všichni lidé a vlastníci</CardFooterLink>
		</OverviewCard>
	);
}
